import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../environments/environment';
import { jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  // 1. 必須在這裡宣告屬性，否則會報錯
  private logoutTimer: any;

  constructor(private cookieService: CookieService) {
    // 程式啟動時，檢查現有 Token 是否已過期
    this.checkTokenAndSetupTimer();
  }

  /**
   * 儲存 Token 並設定自動登出定時器
   * 若舊 Token 存在則會被覆蓋
   */
  setLogonToken(token: string): void {
    console.log('[TokenService] setLogonToken 被調用，token:', token ? '✅' : '❌');
    // 1. 基本防錯：確保傳入的是有效的 JWT 格式 (必須有兩個點)
    if (!token || typeof token !== 'string' || token.split('.').length !== 3) {
      console.error('[TokenService] Invalid token format received');
      return;
    }

    // 2. 先移除舊的 Token 與 清除計時器 (確保乾淨的狀態)
    this.removeLoginToken();
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
    }

    const isLocalhost =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    try {
      const decoded: any = jwtDecode(token);
      console.log('[TokenService] Token 解碼成功，decoded.Id:', decoded.Id);

      // 3. 使用你已經寫好的 getExpireTime 統一取得毫秒數
      const expireTimeMs = this.getExpireTime(decoded);
      let expiresDate: Date | undefined;

      if (expireTimeMs > 0) {
        expiresDate = new Date(expireTimeMs);
      }

      // 4. 寫入新的 Cookie (這會蓋過同名的 Key)
      this.cookieService.set(
        environment.authTokenKey,
        token,
        expiresDate,
        '/',
        undefined,
        isLocalhost ? false : true,
        'Lax',
      );
      console.log('[TokenService] Token 已儲存到 Cookie，expiresDate:', expiresDate);

      // 5. 重新啟動計時器
      this.setupTimer(expiresDate);
    } catch (error) {
      console.error('[TokenService] Token 解析失敗，無法儲存:', error);
      this.logout();
    }
  }

  /**
   * 取得 Token
   */
  getLoginToken(): string | null {
    const token = this.cookieService.get(environment.authTokenKey);
    return token && token !== '' ? token : null;
  }

  /**
   * 移除 Token
   */
  removeLoginToken(): void {
    this.cookieService.delete(environment.authTokenKey, '/');
  }

  /**
   * 判斷登入狀態：若過期則直接登出
   */
  getLoginStaNum(): string | null {
    const token = this.getLoginToken();
    if (!token) return null;

    try {
      const decoded: any = jwtDecode(token);
      const expireTime = this.getExpireTime(decoded); // 使用統一方法

      if (expireTime > 0 && Date.now() > expireTime) {
        this.logout();
        return null;
      }
      return decoded.Id || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * 執行登出動作
   */
  logout(): void {
    this.removeLoginToken();
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
    }
    console.log('Token 已過期或失效，自動登出');
    // 導向至登入頁面
    window.location.href = 'https://i.nccu.edu.tw/login.aspx';
  }

  /**
   * 內部方法：檢查現有 Token 並設定定時器
   */
  private checkTokenAndSetupTimer(): void {
    const token = this.getLoginToken();
    if (token) {
      try {
        const decoded: any = jwtDecode(token);
        const expireTime = this.getExpireTime(decoded); // 使用統一方法
        console.log('[TokenService] 檢查 Token，過期時間:', expireTime);
        if (expireTime > 0) {
          this.setupTimer(new Date(expireTime));
        } else {
          this.logout(); // 沒過期時間也視為無效
        }
      } catch {
        this.logout();
      }
    }
  }

  /**
   * 內部方法：根據過期時間設定 setTimeout
   */
  private setupTimer(expiresDate: Date | undefined): void {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
    }

    if (!expiresDate) return;

    const timeout = expiresDate.getTime() - Date.now();
    console.log('[TokenService] 設定登出定時器，過期時間:', expiresDate, '剩餘毫秒:', timeout);
    if (timeout <= 0 || timeout === undefined) {
      this.logout();
    } else {
      // 設定在過期那一刻觸發登出
      this.logoutTimer = setTimeout(() => {
        this.logout();
      }, timeout);
    }
  }

  /**
   * 取得解析後的 JWT 使用者資訊
   */
  getUserInfo<T = any>(): T | null {
    const token = this.getLoginToken();
    if (!token) return null;
    try {
      return jwtDecode<T>(token);
    } catch {
      return null;
    }
  }

  private getExpireTime(decoded: any): number {
    // 統一讀取順序：Exp (C#屬性) > exp (標準屬性) > Expired (舊屬性)
    const rawExp = decoded.Exp || decoded.exp;

    if (!rawExp) return 0;

    const expireTimeMs =
      typeof rawExp === 'string' && rawExp.includes('/Date(')
        ? parseInt(rawExp.replace(/\/Date\((\d+)\)\//, '$1'), 10)
        : rawExp * 1000;

    console.log('[Debug Time]', {
      Token過期時間: new Date(expireTimeMs).toLocaleString(),
      現在系統時間: new Date().toLocaleString(),
      相差秒數: (expireTimeMs - Date.now()) / 1000,
    });

    return expireTimeMs;
  }
}
